import { useState } from "react";
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from "recharts";
import { SensitiveValue } from '@/components/privacy/SensitiveText';
import { monumentaChartColors, monumentaChartTheme } from '@/lib/components/charts/monumenta-chart-theme';

interface CostDistributionData {
  folha: number
  custosDiretos: number
  custosADM: number
  impostos: number
}

interface CostDistributionChartProps {
  data?: CostDistributionData
}

const INACTIVE_COLOR = '#3A3A3A';

export function CostDistributionChart({ data: inputData }: CostDistributionChartProps) {
  const [activeIndex, setActiveIndex] = useState<number | null>(null);

  // Log the breakdown of costs
  if (inputData) {
    const totalCosts = inputData.folha + inputData.custosDiretos + inputData.custosADM + inputData.impostos;
    console.log('=== COST DISTRIBUTION BREAKDOWN ===');
    console.log({
      folha: `R$ ${(inputData.folha / 1000000).toFixed(2)}M (${((inputData.folha / totalCosts) * 100).toFixed(1)}%)`,
      custosDiretos: `R$ ${(inputData.custosDiretos / 1000000).toFixed(2)}M (${((inputData.custosDiretos / totalCosts) * 100).toFixed(1)}%)`,
      custosADM: `R$ ${(inputData.custosADM / 1000000).toFixed(2)}M (${((inputData.custosADM / totalCosts) * 100).toFixed(1)}%)`,
      impostos: `R$ ${(inputData.impostos / 1000000).toFixed(2)}M (${((inputData.impostos / totalCosts) * 100).toFixed(1)}%)`,
      TOTAL: `R$ ${(totalCosts / 1000000).toFixed(2)}M`
    });
    console.log('====================================\n');
  }

  // Cost shades — all red-based to match platform convention (costs = red)
  const COST_SHADES = ['#F87171', '#EF4444', '#DC2626', '#B91C1C']

  const chartData = inputData ? [
    { name: "Folha", value: inputData.folha, color: COST_SHADES[0] },
    { name: "Terceiros", value: inputData.custosDiretos, color: COST_SHADES[1] },
    { name: "Custos ADM", value: inputData.custosADM, color: COST_SHADES[2] },
    { name: "Impostos", value: inputData.impostos, color: COST_SHADES[3] }
  ] : [
    { name: "Equipe Fixa", value: 1200000, color: COST_SHADES[0] },
    { name: "Freelas", value: 450000, color: COST_SHADES[1] },
    { name: "Parceiros", value: 320000, color: COST_SHADES[2] },
    { name: "Impostos", value: 280000, color: COST_SHADES[3] },
    { name: "Despesas", value: 150000, color: '#991B1B' }
  ];
  // Determine the best scale based on the largest value
  const maxValue = Math.max(...chartData.map(item => item.value));
  const getScale = () => {
    if (maxValue >= 1_000_000_000) return { divisor: 1_000_000_000, suffix: 'B' }; // Billions
    if (maxValue >= 1_000_000) return { divisor: 1_000_000, suffix: 'M' }; // Millions
    if (maxValue >= 1_000) return { divisor: 1_000, suffix: 'k' }; // Thousands
    return { divisor: 1, suffix: '' }; // Raw value
  };
  
  const scale = getScale();
  
  const formatCurrency = (value: number) => {
    const scaled = (value / scale.divisor).toFixed(1);
    // Remove .0 if it's a whole number
    const formatted = scaled.endsWith('.0') ? scaled.slice(0, -2) : scaled;
    return `R$ ${formatted}${scale.suffix}`;
  };

  const total = chartData.reduce((acc, item) => acc + item.value, 0);
  const renderCustomLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, value }: any) => {
    const RADIAN = Math.PI / 180;
    const radius = outerRadius + 24;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);
    const percent = ((value / total) * 100).toFixed(1);
    return (
      <text x={x} y={y} fill={monumentaChartColors.text.secondary} textAnchor="middle" dominantBaseline="central" fontSize={10}>
        {percent}%
      </text>
    );
  };

  return (
    <div className="bg-brand-dark-light border border-brand-dark-lighter rounded-monumenta p-6 card-hover shadow-monumenta">
      <div className="mb-6">
        <h3 className="text-white font-display text-xl font-semibold">Distribuição de Custos</h3>
      </div>

      <ResponsiveContainer width="100%" height={300}>
        <PieChart>
          <Pie
            data={chartData}
            cx="50%"
            cy="50%"
            labelLine={false}
            label={renderCustomLabel}
            outerRadius={100}
            innerRadius={60}
            fill="#8884d8"
            dataKey="value"
            paddingAngle={2}
            isAnimationActive={false}
            onMouseEnter={(_, index) => setActiveIndex(index)}
            onMouseLeave={() => setActiveIndex(null)}
          >
            {chartData.map((entry, index) => (
              <Cell
                key={`cell-${index}`}
                fill={activeIndex !== null && activeIndex !== index ? INACTIVE_COLOR : entry.color}
                stroke="none"
                style={{ transition: 'fill 0.2s ease' }}
              />
            ))}
          </Pie>
          <Tooltip
            content={({ active, payload }) => {
              if (active && payload && payload.length) {
                return (
                  <div style={monumentaChartTheme.tooltip.contentStyle}>
                    <p style={{ color: monumentaChartColors.text.primary, fontWeight: "600", marginBottom: "5px", fontSize: '11px' }}>{payload[0].name}</p>
                    <p style={{ color: '#F87171', fontSize: '11px' }}>
                      <SensitiveValue mask="R$ ••••••">
                        {formatCurrency(payload[0].value as number)}
                      </SensitiveValue>
                    </p>
                  </div>
                );
              }
              return null;
            }}
          />
          <Legend
            verticalAlign="bottom"
            height={36}
            formatter={(value, entry: any) => (
              <span style={{ fontSize: '11px', color: monumentaChartColors.text.secondary }}>
                {value}: <SensitiveValue mask="R$ ••••••">
                  {formatCurrency(entry.payload.value)}
                </SensitiveValue>
              </span>
            )}
          />
        </PieChart>
      </ResponsiveContainer>
    </div>
  );
}
